import { Card } from 'components/common';
import {
  DialogFormWrapper,
  FormActionsStyle,
  FormBody,
  FormGroupTitle,
  FormHeader,
  FormRow,
  FormTitle,
} from 'components/common/Forms/Forms.styled';
import { Col } from 'components/common/Grid';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaCheckCircle, FaCommentDots } from 'react-icons/fa';
import { CommonButton } from '../../../../components/common/Forms/Button';
import { CommonInput } from '../../../../components/common/Forms/Input';
import { CardLoader } from '../../../../components/common/Loading';
import { useConfirmDeleteDialog } from '../../../../components/Popup/ConfirmDialog/confirmDeleteDialogHook';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { useMutateUpdateTrust } from '../../../../query/trusts';
import { TrustData } from '../../../../types/trusts';
import { successToast } from '../../../../utils/toast';

type Props = {
  trust: TrustData;
};

type TextsFormValues = {
  vonage_application_id: string;
  vonage_secret: string;
  vonage_webhook_secret: string;
  vonage_rcs_sender_id: string;
  vonage_sms_sender_id: string;
};

const VONAGE_SMS_SENDER_ID_PATTERN = /^[A-Za-z0-9 .&_-]+$/;
const VONAGE_SMS_SENDER_ID_LETTER_PATTERN = /[A-Za-z]/;
const VONAGE_SMS_SENDER_ID_MAX_LENGTH = 11;

const emptyTextsFormValues = (): TextsFormValues => ({
  vonage_application_id: '',
  vonage_rcs_sender_id: '',
  vonage_sms_sender_id: '',
  vonage_secret: '',
  vonage_webhook_secret: '',
});

const textsFormValuesFromTrust = (trust: TrustData): TextsFormValues => ({
  vonage_application_id: trust.vonage_application_id ?? '',
  vonage_rcs_sender_id: trust.vonage_rcs_sender_id ?? '',
  vonage_sms_sender_id: trust.vonage_sms_sender_id ?? '',
  vonage_secret: '',
  vonage_webhook_secret: '',
});

export const TrustTextsPreferences = ({ trust }: Props) => {
  const { t } = useCommonTranslation();
  const { confirmDelete } = useConfirmDeleteDialog();
  const isConfigured = Boolean(trust.vonage_application_id);
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TextsFormValues>({
    defaultValues: textsFormValuesFromTrust(trust),
  });

  const { mutate: updateTrust, isLoading } = useMutateUpdateTrust({
    onSuccess: (_data, variables) => {
      const isDelete = variables.vonage_application_id === '';
      successToast(
        t(
          isDelete
            ? 'trust-texts-delete-success'
            : 'trust-texts-update-success',
        ),
      );
      if (isDelete) {
        reset(emptyTextsFormValues());
      }
      setIsEditing(false);
    },
  });

  const handleStartEditing = useCallback(() => {
    reset(textsFormValuesFromTrust(trust));
    setIsEditing(true);
  }, [reset, trust]);

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleFormSubmit = useCallback(
    (data: TextsFormValues) => {
      updateTrust({ ...trust, ...data });
    },
    [updateTrust, trust],
  );

  const handleDelete = useCallback(() => {
    confirmDelete({
      onConfirm: () =>
        updateTrust({
          ...trust,
          vonage_application_id: '',
          vonage_secret: '',
          vonage_webhook_secret: '',
          vonage_rcs_sender_id: '',
          vonage_sms_sender_id: '',
        }),
      title: t('confirm-delete-trust-texts'),
      description: t('confirm-delete-trust-texts-desc'),
    });
  }, [confirmDelete, t, updateTrust, trust]);

  const requiredRule = { required: t('must-not-empty') as string };
  const vonageSmsSenderIdValidationMessage = t(
    'vonage-sms-sender-id-validation',
  ) as string;
  const vonageSmsSenderIdRules = {
    ...requiredRule,
    maxLength: {
      value: VONAGE_SMS_SENDER_ID_MAX_LENGTH,
      message: vonageSmsSenderIdValidationMessage,
    },
    pattern: {
      value: VONAGE_SMS_SENDER_ID_PATTERN,
      message: vonageSmsSenderIdValidationMessage,
    },
    validate: (value: string) =>
      VONAGE_SMS_SENDER_ID_LETTER_PATTERN.test(value) ||
      vonageSmsSenderIdValidationMessage,
  };

  return (
    <Col md={6}>
      <Card>
        <DialogFormWrapper>
          <FormHeader>
            <FaCommentDots />
            <FormTitle>{t('texts-vonage-title')}</FormTitle>
          </FormHeader>
          <FormGroupTitle style={{ textAlign: 'center' }}>
            {t('texts-vonage-description')}
          </FormGroupTitle>

          {isConfigured && !isEditing ? (
            <>
              <FormGroupTitle style={{ textAlign: 'center' }}>
                <FaCheckCircle /> {t('trust-texts-configured')}
              </FormGroupTitle>
              <FormRow style={{ marginTop: 'var(--s2)' }}>
                <CommonInput
                  name="vonage_application_id"
                  label={t('vonage_application_id')}
                  value={trust.vonage_application_id ?? ''}
                  disabled
                />
              </FormRow>
              <FormRow>
                <CommonInput
                  name="vonage_rcs_sender_id"
                  label={t('vonage_rcs_sender_id')}
                  value={trust.vonage_rcs_sender_id ?? ''}
                  disabled
                />
              </FormRow>
              <FormRow>
                <CommonInput
                  name="vonage_sms_sender_id"
                  label={t('vonage_sms_sender_id')}
                  value={trust.vonage_sms_sender_id ?? ''}
                  disabled
                />
              </FormRow>
              <FormActionsStyle align="center">
                <CommonButton
                  variant="primary"
                  type="button"
                  onClick={handleStartEditing}
                >
                  {t('update')}
                </CommonButton>
                <CommonButton
                  variant="danger"
                  type="button"
                  onClick={handleDelete}
                >
                  {t('delete')}
                </CommonButton>
              </FormActionsStyle>
            </>
          ) : (
            <FormBody
              onSubmit={handleSubmit(handleFormSubmit)}
              autoComplete="false"
            >
              <FormRow style={{ marginTop: 'var(--s2)' }}>
                <CommonInput
                  label={t('vonage_application_id')}
                  placeholder={t('vonage_application_id')}
                  helperText={errors.vonage_application_id?.message}
                  hasError={Boolean(errors.vonage_application_id)}
                  {...register('vonage_application_id', requiredRule)}
                />
              </FormRow>
              <FormRow>
                <CommonInput
                  label={t('vonage_secret')}
                  placeholder={t('vonage_secret')}
                  type="password"
                  helperText={errors.vonage_secret?.message}
                  hasError={Boolean(errors.vonage_secret)}
                  {...register('vonage_secret', requiredRule)}
                />
              </FormRow>
              <FormRow>
                <CommonInput
                  label={t('vonage_webhook_secret')}
                  placeholder={t('vonage_webhook_secret')}
                  type="password"
                  helperText={errors.vonage_webhook_secret?.message}
                  hasError={Boolean(errors.vonage_webhook_secret)}
                  {...register('vonage_webhook_secret', requiredRule)}
                />
              </FormRow>
              <FormRow>
                <CommonInput
                  label={t('vonage_rcs_sender_id')}
                  placeholder={t('vonage_rcs_sender_id')}
                  helperText={errors.vonage_rcs_sender_id?.message}
                  hasError={Boolean(errors.vonage_rcs_sender_id)}
                  {...register('vonage_rcs_sender_id', requiredRule)}
                />
              </FormRow>
              <FormRow>
                <CommonInput
                  label={t('vonage_sms_sender_id')}
                  placeholder={t('vonage_sms_sender_id')}
                  maxLength={VONAGE_SMS_SENDER_ID_MAX_LENGTH}
                  helperText={errors.vonage_sms_sender_id?.message}
                  hasError={Boolean(errors.vonage_sms_sender_id)}
                  {...register('vonage_sms_sender_id', vonageSmsSenderIdRules)}
                />
              </FormRow>
              <FormActionsStyle align="center">
                {isEditing && (
                  <CommonButton
                    variant="secondary"
                    type="button"
                    onClick={handleCancelEditing}
                  >
                    {t('cancel')}
                  </CommonButton>
                )}
                <CommonButton variant="primary" type="submit">
                  {isConfigured ? t('update') : t('create')}
                </CommonButton>
              </FormActionsStyle>
            </FormBody>
          )}
        </DialogFormWrapper>
        {isLoading && <CardLoader fillWrapper={true} />}
      </Card>
    </Col>
  );
};
